import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  Person,
  School,
  TempleHindu,
  AccountBalance,
  Business,
  Groups,
  Gavel,
  Create,
  Newspaper,
  Book,
  Tour,
  VolunteerActivism,
  HealthAndSafety,
  Balance,
  Psychology,
  Warning,
} from '@mui/icons-material';

const UseCasesComponent: React.FC = () => {
  const { t } = useTranslation(['useCases', 'seo']);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <SEO
        title={t('seo:useCases.title', { defaultValue: 'Use Cases | Sarvalok AI Applications' })}
        description={t('seo:useCases.description', { defaultValue: 'Explore diverse applications of Sarvalok AI across education, research, spiritual study, cultural preservation, and authentic knowledge discovery.' })}
        url="/use-cases"
      />
      <h1 className="text-3xl font-bold text-center mb-6">{t('useCases.title')}</h1>
      <p className="text-lg text-center mb-8">
        {t('useCases.introduction')}
      </p>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Person className="mr-2" /> {t('useCases.individualSeekers.title')}
          </h2>
          <p className="mt-2">{t('useCases.individualSeekers.subtitle')}</p>
          <p>
            {t('useCases.individualSeekers.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.individualSeekers.benefits.personalGrowth')}</li>
            <li>{t('useCases.individualSeekers.benefits.spiritualPractice')}</li>
            <li>{t('useCases.individualSeekers.benefits.lifeGuidance')}</li>
            <li>{t('useCases.individualSeekers.benefits.mindfulness')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.individualSeekers.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <School className="mr-2" /> {t('useCases.educationalInstitutions.title')}
          </h2>
          <p className="mt-2">{t('useCases.educationalInstitutions.subtitle')}</p>
          <p>
            {t('useCases.educationalInstitutions.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.educationalInstitutions.benefits.curriculumDevelopment')}</li>
            <li>{t('useCases.educationalInstitutions.benefits.researchSupport')}</li>
            <li>{t('useCases.educationalInstitutions.benefits.studentEngagement')}</li>
            <li>{t('useCases.educationalInstitutions.benefits.culturalEducation')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.educationalInstitutions.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <AccountBalance className="mr-2" /> {t('useCases.spiritualCenters.title')}
          </h2>
          <p className="mt-2">{t('useCases.spiritualCenters.subtitle')}</p>
          <p>
            {t('useCases.spiritualCenters.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.spiritualCenters.benefits.teachingSupport')}</li>
            <li>{t('useCases.spiritualCenters.benefits.programDevelopment')}</li>
            <li>{t('useCases.spiritualCenters.benefits.communityEngagement')}</li>
            <li>{t('useCases.spiritualCenters.benefits.authenticity')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.spiritualCenters.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <TempleHindu className="mr-2" /> {t('useCases.spiritualCentersYoga.title')}
          </h2>
          <p className="mt-2">{t('useCases.spiritualCentersYoga.subtitle')}</p>
          <p>
            {t('useCases.spiritualCentersYoga.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.spiritualCentersYoga.benefits.complementPractices')}</li>
            <li>{t('useCases.spiritualCentersYoga.benefits.onDemandKnowledge')}</li>
            <li>{t('useCases.spiritualCentersYoga.benefits.culturalRelevance')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.spiritualCentersYoga.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Business className="mr-2" /> {t('useCases.corporations.title')}
          </h2>
          <p className="mt-2">{t('useCases.corporations.subtitle')}</p>
          <p>
            {t('useCases.corporations.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.corporations.benefits.employeeTraining')}</li>
            <li>{t('useCases.corporations.benefits.leadershipDevelopment')}</li>
            <li>{t('useCases.corporations.benefits.mentalWellness')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.corporations.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Groups className="mr-2" /> {t('useCases.culturalOrganizations.title')}
          </h2>
          <p className="mt-2">{t('useCases.culturalOrganizations.subtitle')}</p>
          <p>
            {t('useCases.culturalOrganizations.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.culturalOrganizations.benefits.culturalProgramming')}</li>
            <li>{t('useCases.culturalOrganizations.benefits.publicEngagement')}</li>
            <li>{t('useCases.culturalOrganizations.benefits.heritagePreservation')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.culturalOrganizations.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Gavel className="mr-2" /> {t('useCases.governments.title')}
          </h2>
          <p className="mt-2">{t('useCases.governments.subtitle')}</p>
          <p>
            {t('useCases.governments.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.governments.benefits.policyFormulation')}</li>
            <li>{t('useCases.governments.benefits.ethicalGovernance')}</li>
            <li>{t('useCases.governments.benefits.publicAwareness')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.governments.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Create className="mr-2" /> {t('useCases.contentCreators.title')}
          </h2>
          <p className="mt-2">{t('useCases.contentCreators.subtitle')}</p>
          <p>
            {t('useCases.contentCreators.description')}
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>{t('useCases.contentCreators.benefits.contentCreation')}</li>
            <li>{t('useCases.contentCreators.benefits.courseDevelopment')}</li>
            <li>{t('useCases.contentCreators.benefits.socialMediaEngagement')}</li>
          </ul>
          <p className="mt-2 italic">
            {t('useCases.contentCreators.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Newspaper className="mr-2" /> {t('useCases.media.title')}
          </h2>
          <p className="mt-2">{t('useCases.media.subtitle')}</p>
          <p>
            {t('useCases.media.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.media.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Book className="mr-2" /> {t('useCases.artScholars.title')}
          </h2>
          <p className="mt-2">{t('useCases.artScholars.subtitle')}</p>
          <p>
            {t('useCases.artScholars.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.artScholars.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Tour className="mr-2" /> {t('useCases.tourism.title')}
          </h2>
          <p className="mt-2">{t('useCases.tourism.subtitle')}</p>
          <p>
            {t('useCases.tourism.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.tourism.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <VolunteerActivism className="mr-2" /> {t('useCases.developmentAgencies.title')}
          </h2>
          <p className="mt-2">{t('useCases.developmentAgencies.subtitle')}</p>
          <p>
            {t('useCases.developmentAgencies.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.developmentAgencies.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <HealthAndSafety className="mr-2" /> {t('useCases.publicHealth.title')}
          </h2>
          <p className="mt-2">{t('useCases.publicHealth.subtitle')}</p>
          <p>
            {t('useCases.publicHealth.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.publicHealth.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Balance className="mr-2" /> {t('useCases.socialJustice.title')}
          </h2>
          <p className="mt-2">{t('useCases.socialJustice.subtitle')}</p>
          <p>
            {t('useCases.socialJustice.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.socialJustice.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Psychology className="mr-2" /> {t('useCases.conflictResolution.title')}
          </h2>
          <p className="mt-2">{t('useCases.conflictResolution.subtitle')}</p>
          <p>
            {t('useCases.conflictResolution.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.conflictResolution.example')}
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold flex items-center">
            <Warning className="mr-2" /> {t('useCases.conflictPrevention.title')}
          </h2>
          <p className="mt-2">{t('useCases.conflictPrevention.subtitle')}</p>
          <p>
            {t('useCases.conflictPrevention.description')}
          </p>
          <p className="mt-2 italic">
            {t('useCases.conflictPrevention.example')}
          </p>
        </section>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-semibold">{t('useCases.conclusion.title')}</h2>
        <p className="mt-2">
          {t('useCases.conclusion.description')}
        </p>
      </div>
    </div>
  );
};

export default UseCasesComponent;
